import { useCallback, useLayoutEffect, type ReactNode } from "react";
import { CheckCircle2, Circle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { FormattedFeedText } from "@/features/feed/FormattedFeedText";
import { cn } from "@/lib/utils";
import { useRightRail } from "@/context/RightRailContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useKaiChat } from "@/features/kai/KaiChatContext";
import { KaiSidePanel, KaiSidePanelActions } from "@/features/kai/KaiSidePanel";
import { RateSurchargeCard } from "@/features/rates/RateSurchargeCard";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { BookingKaiInsightRailSection } from "@/features/bookings/BookingKaiInsightRail";
import { BookingDocumentsRailSection } from "@/features/bookings/BookingDocumentsRail";
import { BookingBlGroupRailSection } from "@/features/bookings/BookingBlGroupRail";
import { BookingAssignedRateRailSection } from "@/features/bookings/BookingAssignedRateRail";
import { BookingMatchedScheduleRailSection } from "@/features/bookings/BookingMatchedScheduleRail";
import { useWorkspaceRail } from "@/hooks/useWorkspaceRail";
import { useRailStackLayout } from "@/hooks/useRailStackLayout";
import { NotesList } from "@/features/workspace/NotesList";
import type { WorkspaceTodo } from "@/types/workspace";
import { useFeed } from "@/context/FeedContext";
import { feedPrivacyScope } from "@/features/feed/feedPrivacy";
import { eventIcon, formatRelativeTime } from "@/features/feed/feedVisuals";
import {
  CollapsibleRailSection,
  RailKaiMount,
} from "./CollapsibleRailSection";
import {
  RailStackProvider,
  railCardClassName,
  railCardInnerClassName,
  railPanelView,
  railSectionStackClassName,
  type RailPanelView,
} from "./RailStackContext";
import { NotificationsPanel } from "./NotificationsPanel";
import { RailStackCardMount } from "./RailStackCardMount";
import {
  railStackShareGrow,
  RAIL_PANEL_ORDER,
  type RailStackPanelId,
} from "./railStackLayout";
import type { PageRailMeta } from "@/context/RightRailContext";

/**
 * Header + portal target for a booking-detail primary content card
 * (Opportunities / Lookup / B/L document). The card body is portaled in by the
 * owning rail component via {@link useBookingRailSlot}; here we only render the
 * collapsible chrome and the slot's portal mount node.
 */
function RailWorkspaceCard({
  active,
  stackAnimate,
  pinBottom = false,
  pinExpanded,
  grow = false,
  view,
  children,
}: {
  active: boolean;
  /** Desktop fixed-height stack (not the mobile drawer `stack` prop). */
  stackAnimate: boolean;
  pinBottom?: boolean;
  /** When unset, pinned + expanded cards stretch (Notes passes `false`). */
  pinExpanded?: boolean;
  grow?: boolean;
  view: RailPanelView;
  children: ReactNode;
}) {
  const resolvedPinExpanded = pinExpanded ?? (pinBottom && view.expanded);
  const cardOptions = { pinBottom, pinExpanded: resolvedPinExpanded, grow };
  return (
    <RailStackCardMount
      active={active}
      stack={stackAnimate}
      grow={grow}
      pinBottom={pinBottom}
      pinExpanded={resolvedPinExpanded}
    >
      <aside className={railCardClassName(view, cardOptions)}>
        <div className={railCardInnerClassName(view, cardOptions)}>
          {children}
        </div>
      </aside>
    </RailStackCardMount>
  );
}

function BookingSlotCard({
  meta,
  view,
  stack,
  stackAnimate,
  grow = false,
  onToggle,
  registerTarget,
}: {
  meta: PageRailMeta | null;
  view: RailPanelView;
  stack: boolean;
  stackAnimate: boolean;
  grow?: boolean;
  onToggle: () => void;
  registerTarget: (node: HTMLElement | null) => void;
}) {
  return (
    <RailWorkspaceCard
      active={view.present && meta != null}
      stackAnimate={stackAnimate}
      grow={grow}
      view={view}
    >
      {meta && (
        <CollapsibleRailSection
          title={meta.title}
          icon={meta.icon}
          badge={meta.badge}
          expanded={view.expanded}
          accordion={view.accordion}
          fill={view.fill}
          flow={view.flow}
          scroll={view.scroll}
          onToggle={onToggle}
          className={railSectionStackClassName(view, {
            mobileStack: stack,
            stackAnimate,
            stackGrow: grow,
          })}
          contentClassName={stack ? undefined : "pr-0.5"}
        >
          <div
            ref={registerTarget}
            className="rail-stack-portal-target min-h-0 min-w-0 w-full pr-0.5"
          />
        </CollapsibleRailSection>
      )}
    </RailWorkspaceCard>
  );
}

/** Live "recent activity" — the last few root events from the domain feed. */
function RecentActivityWidget() {
  const { events, loading } = useFeed();
  const recent = events.slice(0, 6);

  if (loading && recent.length === 0) {
    return <p className="text-xs text-muted-foreground">Loading activity…</p>;
  }
  if (recent.length === 0) {
    return <p className="text-xs text-muted-foreground">No recent activity.</p>;
  }

  return (
    <ul className="space-y-2.5">
      {recent.map((item) => {
        const Icon = eventIcon(item.event_type);
        return (
          <li key={item.id} className="flex items-start gap-2 text-sm">
            <Icon className="mt-0.5 size-3.5 shrink-0 text-primary/70" />
            <div className="min-w-0">
              <p className="leading-tight">
                <FormattedFeedText
                  text={item.title}
                  scope={feedPrivacyScope(item)}
                />
              </p>
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(item.created_at)}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const RAIL_HEIGHT = "h-full min-h-0";

function Widget({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {badge != null && (
          <Badge variant="info" className="px-1.5 py-0 text-[10px]">
            {badge}
          </Badge>
        )}
      </div>
      {children}
    </section>
  );
}

function TodoList({
  todos,
  loading,
}: {
  todos: WorkspaceTodo[];
  loading: boolean;
}) {
  if (loading && todos.length === 0) {
    return <p className="text-xs text-muted-foreground">Loading to-do…</p>;
  }
  if (todos.length === 0) {
    return <p className="text-xs text-muted-foreground">Nothing to do right now.</p>;
  }
  return (
    <ul className="space-y-2">
      {todos.map((todo) => (
        <li key={todo.id} className="flex items-center gap-2 text-sm">
          {todo.done ? (
            <CheckCircle2 className="size-4 shrink-0 text-primary" />
          ) : (
            <Circle className="size-4 shrink-0 text-muted-foreground" />
          )}
          <Link
            to={todo.href}
            className={cn(
              "min-w-0 truncate hover:underline",
              todo.done && "text-muted-foreground line-through decoration-1",
            )}
          >
            {todo.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** The stacked widget content, reused in the desktop aside and mobile sheet. */
export function RightRailContent({
  kaiPage = false,
  showNotifications = true,
  showPageRail = true,
  showWorkspace = true,
  stack = false,
}: {
  kaiPage?: boolean;
  showNotifications?: boolean;
  showPageRail?: boolean;
  /** Notes / to-do / recent activity. Shown collapsed at the bottom on feed. */
  showWorkspace?: boolean;
  /** Grow with content so a parent scrollport (mobile sheet) can pan. */
  stack?: boolean;
}) {
  const {
    kaiVisible,
    kaiAnimating,
    pageRailMeta,
    opportunityRailMeta,
    lookupRailMeta,
    blDocumentRailMeta,
    bookingInsightSourceId,
    bookingBlGroupSourceId,
    bookingAssignedRateRail,
    bookingMatchedScheduleRail,
    notificationsPanelActive,
    selectedRateLine,
    closeRateLineDetail,
    toggleKaiSection,
    toggleNotesSection,
    togglePageRailSection,
    pageRailHeaderActions,
    registerPageRailTarget,
    registerBookingRailTarget,
    toggleBookingRailSection,
    setDesktopRailStack,
  } = useRightRail();

  // Stable per-slot ref callbacks + toggles so the portal mount node is not
  // torn down and re-created on every render.
  const registerOpportunityTarget = useCallback(
    (node: HTMLElement | null) => registerBookingRailTarget("opportunity", node),
    [registerBookingRailTarget],
  );
  const registerLookupTarget = useCallback(
    (node: HTMLElement | null) => registerBookingRailTarget("lookup", node),
    [registerBookingRailTarget],
  );
  const registerBlDocumentTarget = useCallback(
    (node: HTMLElement | null) => registerBookingRailTarget("blDocument", node),
    [registerBookingRailTarget],
  );
  const toggleOpportunity = useCallback(
    () => toggleBookingRailSection("opportunity"),
    [toggleBookingRailSection],
  );
  const toggleLookup = useCallback(
    () => toggleBookingRailSection("lookup"),
    [toggleBookingRailSection],
  );
  const toggleBlDocument = useCallback(
    () => toggleBookingRailSection("blDocument"),
    [toggleBookingRailSection],
  );
  const { preferences } = useUserPreferences();
  const { notes, todos, loading: workspaceLoading } = useWorkspaceRail();
  const kai = useKaiChat();
  const historyOpen = kai?.historyOpen ?? false;
  const kaiTitle = historyOpen
    ? "Chat history"
    : (kai?.conversations.find((c) => c.id === kai.activeId)?.title ?? "Kai");

  const { layout } = useRailStackLayout({
    stack,
    showWorkspace,
    showPageRail,
    kaiPage,
  });
  const railStackDesktop = layout.mode === "stacked";

  useLayoutEffect(() => {
    setDesktopRailStack(railStackDesktop);
  }, [railStackDesktop, setDesktopRailStack]);

  const notificationsActive = notificationsPanelActive;
  const rateLineActive = selectedRateLine != null;

  const pageView = railPanelView(layout, "page");
  const opportunityView = railPanelView(layout, "opportunity");
  const lookupView = railPanelView(layout, "lookup");
  const blDocumentView = railPanelView(layout, "blDocument");
  const blGroupView = railPanelView(layout, "blGroup");
  const assignedRateView = railPanelView(layout, "assignedRate");
  const matchedScheduleView = railPanelView(layout, "matchedSchedule");
  const documentsView = railPanelView(layout, "documents");
  const insightView = railPanelView(layout, "insight");
  const kaiView = railPanelView(layout, "kai");
  const notesView = railPanelView(layout, "notes");

  const stackGrow = (id: RailStackPanelId) =>
    railStackShareGrow(layout, railStackDesktop, id);
  const upperRailPresent = RAIL_PANEL_ORDER.some(
    (id) => id !== "notes" && layout.panels[id].present,
  );

  const notificationsPanel = showNotifications && notificationsActive && (
    <NotificationsPanel
      fill
      className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col"
    />
  );

  const stackMainCards = (
    <>
        <RailWorkspaceCard
          active={pageView.present && pageRailMeta != null}
          stackAnimate={railStackDesktop}
          grow={stackGrow("page")}
          view={pageView}
        >
          {pageRailMeta && (
            <CollapsibleRailSection
              title={pageRailMeta.title}
              icon={pageRailMeta.icon}
              badge={pageRailMeta.badge}
              badgeSuffix={pageRailMeta.badgeSuffix}
              actions={pageRailHeaderActions}
              expanded={pageView.expanded}
              accordion={pageView.accordion}
              fill={pageView.fill}
              flow={pageView.flow}
              scroll={pageView.scroll}
              onToggle={togglePageRailSection}
              className={railSectionStackClassName(pageView, {
                mobileStack: stack,
                stackAnimate: railStackDesktop,
                stackGrow: stackGrow("page"),
              })}
              contentClassName={stack ? undefined : "pr-0.5"}
            >
              <div
                ref={registerPageRailTarget}
                className="rail-stack-portal-target min-h-0 min-w-0 w-full pr-0.5"
              />
            </CollapsibleRailSection>
          )}
        </RailWorkspaceCard>

        <BookingSlotCard
          meta={opportunityRailMeta}
          view={opportunityView}
          stack={stack}
          stackAnimate={railStackDesktop}
          grow={stackGrow("opportunity")}
          onToggle={toggleOpportunity}
          registerTarget={registerOpportunityTarget}
        />

        <BookingSlotCard
          meta={lookupRailMeta}
          view={lookupView}
          stack={stack}
          stackAnimate={railStackDesktop}
          grow={stackGrow("lookup")}
          onToggle={toggleLookup}
          registerTarget={registerLookupTarget}
        />

        <BookingSlotCard
          meta={blDocumentRailMeta}
          view={blDocumentView}
          stack={stack}
          stackAnimate={railStackDesktop}
          grow={stackGrow("blDocument")}
          onToggle={toggleBlDocument}
          registerTarget={registerBlDocumentTarget}
        />

        <RailWorkspaceCard
          active={blGroupView.present && bookingBlGroupSourceId != null}
          stackAnimate={railStackDesktop}
          grow={stackGrow("blGroup")}
          view={blGroupView}
        >
          {bookingBlGroupSourceId && (
            <BookingBlGroupRailSection
              sourceId={bookingBlGroupSourceId}
              stack={stack}
              stackAnimate={railStackDesktop}
              stackGrow={stackGrow("blGroup")}
            />
          )}
        </RailWorkspaceCard>

        <RailWorkspaceCard
          active={assignedRateView.present && bookingAssignedRateRail != null}
          stackAnimate={railStackDesktop}
          grow={stackGrow("assignedRate")}
          view={assignedRateView}
        >
          {bookingAssignedRateRail && (
            <BookingAssignedRateRailSection
              payload={bookingAssignedRateRail}
              stack={stack}
              stackAnimate={railStackDesktop}
              stackGrow={stackGrow("assignedRate")}
            />
          )}
        </RailWorkspaceCard>

        <RailWorkspaceCard
          active={
            matchedScheduleView.present && bookingMatchedScheduleRail != null
          }
          stackAnimate={railStackDesktop}
          grow={stackGrow("matchedSchedule")}
          view={matchedScheduleView}
        >
          {bookingMatchedScheduleRail && (
            <BookingMatchedScheduleRailSection
              sailing={bookingMatchedScheduleRail}
              stack={stack}
              stackAnimate={railStackDesktop}
              stackGrow={stackGrow("matchedSchedule")}
            />
          )}
        </RailWorkspaceCard>

        <RailWorkspaceCard
          active={documentsView.present && bookingInsightSourceId != null}
          stackAnimate={railStackDesktop}
          grow={stackGrow("documents")}
          view={documentsView}
        >
          {bookingInsightSourceId && (
            <BookingDocumentsRailSection
              sourceId={bookingInsightSourceId}
              stack={stack}
              stackAnimate={railStackDesktop}
              stackGrow={stackGrow("documents")}
            />
          )}
        </RailWorkspaceCard>

        <RailWorkspaceCard
          active={insightView.present && bookingInsightSourceId != null}
          stackAnimate={railStackDesktop}
          grow={stackGrow("insight")}
          view={insightView}
        >
          {bookingInsightSourceId && (
            <BookingKaiInsightRailSection
              sourceId={bookingInsightSourceId}
              stack={stack}
              stackScroll={railStackDesktop}
              stackGrow={stackGrow("insight")}
            />
          )}
        </RailWorkspaceCard>

        {kaiView.present && !kaiPage && (
          <RailKaiMount
            open={kaiVisible}
            animating={kaiAnimating}
            className={cn(
              railStackDesktop
                ? cn(
                    "min-h-0 flex flex-col overflow-hidden",
                    stackGrow("kai")
                      ? "rail-stack-card--stretch"
                      : "rail-stack-card--shrink",
                    kaiView.fill && "rail-stack-card--open",
                  )
                : kaiView.fill
                  ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                  : "shrink-0",
            )}
          >
            <aside
              className={
                stack
                  ? cn(
                      "glass-panel relative isolate flex min-h-0 w-full flex-col overflow-clip p-4",
                      "shrink-0",
                    )
                  : cn(
                      "glass-panel relative isolate flex min-h-0 w-full flex-col overflow-clip p-4",
                      kaiView.fill ? "h-full flex-1" : "shrink-0 flex-none",
                    )
              }
            >
              <CollapsibleRailSection
                title={kaiTitle}
                icon={Sparkles}
                expanded={kaiView.expanded}
                accordion={kaiView.accordion}
                fill={kaiView.fill}
                flow={kaiView.flow}
                onToggle={toggleKaiSection}
                actions={<KaiSidePanelActions />}
                className={cn(
                  railSectionStackClassName(kaiView, {
                    mobileStack: stack,
                    stackAnimate: railStackDesktop,
                    stackGrow: stackGrow("kai"),
                  }),
                  kaiView.fill && !railStackDesktop && "flex flex-1 flex-col",
                )}
              >
                <KaiSidePanel />
              </CollapsibleRailSection>
            </aside>
          </RailKaiMount>
        )}
    </>
  );

  const notesCard = () => (
    <RailWorkspaceCard
      active={notesView.present}
      stackAnimate={railStackDesktop}
      grow={stackGrow("notes")}
      pinExpanded={false}
      view={notesView}
    >
      <CollapsibleRailSection
        title="Notes"
        badge={notes.length}
        expanded={notesView.expanded}
        accordion={notesView.accordion}
        fill={notesView.fill}
        flow={notesView.flow}
        scroll={notesView.scroll}
        onToggle={toggleNotesSection}
        className={railSectionStackClassName(notesView, {
          mobileStack: stack,
          stackAnimate: railStackDesktop,
          stackGrow: stackGrow("notes"),
        })}
        contentClassName={stack ? undefined : "pr-0.5"}
      >
        <div className={stack ? undefined : "pr-0.5"}>
          <NotesList
            notes={notes}
            loading={workspaceLoading}
            layout={stack ? "row" : "card"}
          />

          <Separator className="my-3 shrink-0" />

          <div className="space-y-4 pb-1">
            <Widget title="To-do">
              <TodoList todos={todos} loading={workspaceLoading} />
            </Widget>

            <Separator />

            <Widget title="Recent activity">
              <RecentActivityWidget />
            </Widget>
          </div>
        </div>
      </CollapsibleRailSection>
    </RailWorkspaceCard>
  );

  return (
    <RailStackProvider layout={layout}>
      <div
        className={cn(
          "flex w-full min-w-0 max-w-full flex-col gap-[var(--shell-gap)]",
          layout.mode !== "mobileFlow" &&
            cn("min-h-0 flex-1 overflow-hidden", RAIL_HEIGHT),
          layout.mode === "stacked" && "rail-stack-mode",
        )}
      >
        {notificationsPanel}

        {rateLineActive && selectedRateLine && (
          <RateSurchargeCard
            line={selectedRateLine}
            onClose={closeRateLineDetail}
            showLocodes={preferences.showLocodes}
            className={stack ? undefined : "min-h-0 flex-1 overflow-y-auto"}
          />
        )}

        {railStackDesktop ? (
          <div
            className={cn(
              "rail-stack-body-split",
              !upperRailPresent && "rail-stack-body-split--notes-only",
            )}
          >
            <div className="rail-stack-main">
              {stackMainCards}
              {notesCard()}
            </div>
          </div>
        ) : (
          <>
            {stackMainCards}
            {notesCard()}
          </>
        )}
      </div>
    </RailStackProvider>
  );
}

interface RightRailProps {
  className?: string;
  /** Feed page: Kai + Filters + collapsed Notes, never the notification stack. */
  kaiOnly?: boolean;
}

/** Desktop aside wrapper (lg+) — stacked Kai + workspace cards. */
export function RightRail({ className, kaiOnly = false }: RightRailProps) {
  return (
    <div
      className={cn(
        "flex h-full w-full min-w-0 shrink-0 flex-col",
        RAIL_HEIGHT,
        className,
      )}
    >
      {kaiOnly ? (
        <RightRailContent showNotifications={false} showWorkspace />
      ) : (
        <RightRailContent />
      )}
    </div>
  );
}
